/**
 * Instance create/settings Conta root pickers.
 *
 * Two filters share this module:
 * - Exit-rail / matriz Conta root: PERSON/PF only, every status including pending.
 * - Instance owner RootAccountId (settings on partner instances): PF + PJ.
 */

import intl from "react-intl-universal";
import { fetchBaasAccounts } from "../../../../Services/baasAccountsListApi";
import { fetchBaasEntities } from "../../../../Services/baasEntidadesApi";
import { fetchInternalTransferOptions } from "../../../../Services/baasInternalTransfersApi";
import {
  isBaasExitRailVirtualAccount,
  isVirtualInstanceShellAccount,
} from "../../../../Services/baasVirtualShells";

const ROLE_RANK = {
  root: 0,
  operational: 1,
  operating: 1,
  business: 2,
  person: 3,
};

const BLOCKED_ROLES = new Set([
  "overdraft_reserve",
  "program_reserve",
  "segregation_wallet",
  "wallet",
  "otc",
  "otc_virtual",
  "origin_rail",
]);

const BLOCKED_TYPES = new Set(["wallet", "otc", "overdraft", "program", "origin_rail"]);

const KIND_I18N = {
  root: "baas.instances.createModal.rootAccountKindRoot",
  operational: "baas.instances.createModal.rootAccountKindOperational",
  business: "baas.instances.createModal.rootAccountKindBusiness",
  person: "baas.instances.createModal.rootAccountKindPerson",
};

const STATUS_I18N = {
  pending: "baas.instances.createModal.rootAccountStatusPending",
  active: "baas.instances.createModal.rootAccountStatusActive",
  blocked: "baas.instances.createModal.rootAccountStatusBlocked",
  suspended: "baas.instances.createModal.rootAccountStatusBlocked",
};

const tokenList = (account = {}) => [
  account.entityType,
  account.role,
  account.type,
  account.accountType,
  account.account_type,
  account.raw?.account_type,
  account.raw?.type,
];

const hasToken = (account, allowed) =>
  tokenList(account).some((value) => allowed.has(String(value || "").trim().toLowerCase()));

const entityTypeOf = (account) => String(account?.entityType || "").trim().toLowerCase();

const isBusinessEntity = (account) => {
  const type = entityTypeOf(account);
  return type === "pj" || type === "business";
};

const isPersonEntity = (account) => {
  const type = entityTypeOf(account);
  return type === "pf" || type === "person" || type === "personal";
};

const isBusinessHint = (account) => isBusinessEntity(account) || hasToken(account, new Set(["pj", "business"]));

const isPersonHint = (account) => isPersonEntity(account) || hasToken(account, new Set(["pf", "person", "personal"]));

const preferRanked = (left, right) => {
  const leftKey = String(left || "").trim().toLowerCase();
  const rightKey = String(right || "").trim().toLowerCase();
  const leftRank = ROLE_RANK[leftKey] ?? 99;
  const rightRank = ROLE_RANK[rightKey] ?? 99;
  if (rightRank < leftRank) return rightKey || leftKey;
  return leftKey || rightKey;
};

const mergeRootCandidates = (current, incoming) => {
  const currentVirtual = current.isVirtual === true || current.is_virtual === true;
  const incomingVirtual = incoming.isVirtual === true || incoming.is_virtual === true;
  return {
    ...current,
    ...incoming,
    role: preferRanked(current.role, incoming.role) || incoming.role || current.role,
    type: preferRanked(current.type, incoming.type) || incoming.type || current.type,
    entityType: current.entityType || incoming.entityType,
    entityName: current.entityName || incoming.entityName,
    name: current.name || incoming.name || current.entityName || incoming.entityName,
    status: current.status || incoming.status,
    kybStatus: current.kybStatus || incoming.kybStatus,
    verificationStatus: current.verificationStatus || incoming.verificationStatus,
    isVirtual: currentVirtual || incomingVirtual,
    is_virtual: currentVirtual || incomingVirtual,
    raw: incoming.raw || current.raw,
  };
};

export const toBaasAccountExternalId = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^acct_/i.test(raw)) {
    const hex = raw.slice(5).replace(/-/g, "").toLowerCase();
    return hex.length === 32 ? `acct_${hex}` : raw;
  }
  const hex = raw.replace(/-/g, "").toLowerCase();
  if (/^[0-9a-f]{32}$/.test(hex)) return `acct_${hex}`;
  return raw;
};

export const isEligibleBaasRootAccountOption = (account, { personOnly = false } = {}) => {
  const id = String(account?.id || "").trim();
  if (!id) return false;
  if (account?.isVirtual === true || account?.is_virtual === true) return false;
  if (isBaasExitRailVirtualAccount(account)) return false;
  if (isVirtualInstanceShellAccount(account)) return false;

  const role = String(account?.role || "").trim().toLowerCase();
  if (BLOCKED_ROLES.has(role)) return false;

  const type = String(account?.type || "").trim().toLowerCase();
  if (BLOCKED_TYPES.has(type)) return false;

  if (personOnly) {
    if (isBusinessEntity(account)) return false;
    if (!isPersonEntity(account)) return false;
  }

  return true;
};

export const resolveBaasRootAccountOptionRole = (account) => {
  const role = String(account?.role || "").trim().toLowerCase();
  const type = String(account?.type || "").trim().toLowerCase();
  if (role === "root" || type === "root") return "root";
  if (role === "operating" || type === "operational" || type === "operating") {
    if (isBusinessHint(account)) return "business";
    if (isPersonHint(account)) return "person";
    return "operational";
  }
  if (isBusinessHint(account)) return "business";
  if (isPersonHint(account)) return "person";
  return role || type || "operational";
};

const localizeKind = (kind) => {
  const key = KIND_I18N[kind];
  if (!key) return kind;
  const localized = intl.get(key);
  if (localized && localized !== key) return localized;
  return kind;
};

const localizeStatus = (status) => {
  const key = STATUS_I18N[String(status || "").trim().toLowerCase()];
  if (!key) return "";
  const localized = intl.get(key);
  if (localized && localized !== key) return localized;
  return String(status || "").trim().toLowerCase();
};

const resolveOptionStatus = (account) => {
  const status = String(account?.status || "").trim().toLowerCase();
  if (status && status !== "none") return status;
  const kyb = String(account?.kybStatus || account?.verificationStatus || "")
    .trim()
    .toLowerCase();
  if (kyb === "approved" || kyb === "active") return "active";
  if (kyb === "rejected" || kyb === "blocked" || kyb === "suspended") return "blocked";
  if (kyb === "pending" || kyb === "in_review" || kyb === "in-review") return "pending";
  return status || "";
};

export const formatBaasRootAccountOptionLabel = (account, { includeStatus = false } = {}) => {
  const kind = resolveBaasRootAccountOptionRole(account);
  const name =
    String(account?.name || account?.entityName || "").trim() ||
    String(account?.id || "").trim();
  const statusLabel = includeStatus ? localizeStatus(resolveOptionStatus(account)) : "";
  if (statusLabel) return `${name} (${localizeKind(kind)} · ${statusLabel})`;
  return `${name} (${localizeKind(kind)})`;
};

export const buildBaasRootAccountSelectOptions = (accounts = [], { personOnly = false } = {}) => {
  const byId = new Map();
  for (const account of Array.isArray(accounts) ? accounts : []) {
    const id = toBaasAccountExternalId(account?.id);
    if (!id) continue;
    const normalized = { ...account, id };
    const existing = byId.get(id);
    byId.set(id, existing ? mergeRootCandidates(existing, normalized) : normalized);
  }

  const options = [];
  for (const account of byId.values()) {
    if (!isEligibleBaasRootAccountOption(account, { personOnly })) continue;
    const role = resolveBaasRootAccountOptionRole(account);
    options.push({
      id: String(account.id).trim(),
      role,
      status: resolveOptionStatus(account),
      label: formatBaasRootAccountOptionLabel(
        { ...account, role },
        { includeStatus: personOnly },
      ),
    });
  }

  options.sort((left, right) => {
    const leftRank = ROLE_RANK[left.role] ?? 99;
    const rightRank = ROLE_RANK[right.role] ?? 99;
    return leftRank - rightRank || left.label.localeCompare(right.label);
  });
  return options;
};

const mapPersonEntityToRootCandidate = (entity) => {
  const id = toBaasAccountExternalId(entity?.accountId);
  if (!id) return null;
  const status = String(entity?.status || "").trim().toLowerCase();
  return {
    id,
    entityId: entity.id,
    entityType: "pf",
    entityName: entity.name,
    name: entity.name,
    role: "person",
    type: "operational",
    status: status && status !== "none" ? status : "pending",
    kybStatus: entity.kybStatus,
    verificationStatus: entity.verificationStatus,
  };
};

/** Partner instance owner RootAccountId — PF + PJ operational / root rails. */
export const loadBaasRootAccountSelectOptions = async (instance) => {
  if (!instance) return [];

  const [transferOptions, accounts] = await Promise.all([
    fetchInternalTransferOptions(instance).catch(() => ({
      sourceAccounts: [],
      destinationAccounts: [],
    })),
    fetchBaasAccounts(instance, { includeBalances: false }).catch(() => []),
  ]);

  return buildBaasRootAccountSelectOptions([
    ...(Array.isArray(transferOptions?.sourceAccounts) ? transferOptions.sourceAccounts : []),
    ...(Array.isArray(transferOptions?.destinationAccounts)
      ? transferOptions.destinationAccounts
      : []),
    ...(Array.isArray(accounts) ? accounts : []),
  ]);
};

/**
 * Matriz exit-rail Conta root — PERSON/PF accounts only, every KYC/conta status
 * including pending. Does not use transfer-options (those omit pending / label PJ as person).
 */
export const loadBaasExitRailRootAccountSelectOptions = async (instance) => {
  if (!instance) return [];

  const entities = await fetchBaasEntities(instance, { entityType: "PERSON" }).catch(() => []);
  const personEntities = (Array.isArray(entities) ? entities : []).filter(
    (entity) => entity?.type === "pf" && entity?.id,
  );

  const accounts = await fetchBaasAccounts(instance, {
    entities: personEntities,
    includeBalances: false,
  }).catch(() => []);

  const fromEntities = personEntities.map(mapPersonEntityToRootCandidate).filter(Boolean);

  return buildBaasRootAccountSelectOptions(
    [
      ...(Array.isArray(accounts) ? accounts : []),
      ...fromEntities,
    ],
    { personOnly: true },
  );
};
